import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getTargetMessageInfo } from '../utils';

/**
 * Renders conversation history with proper formatting
 */
export const ConversationHistoryRenderer = ({ history, respondedToIndex }) => {
  const { targetIndex, normalizedHistory } = useMemo(() => {
    return getTargetMessageInfo(history, respondedToIndex);
  }, [history, respondedToIndex]);

  if (!history || !Array.isArray(history) || history.length === 0 || targetIndex < 0) {
    return null;
  }

  let messagesToShow = normalizedHistory;
  let displayTargetIndex = targetIndex;
  
  if (targetIndex >= 0 && targetIndex < 2 && history.length > 2) {
    messagesToShow = [...history].reverse();
    displayTargetIndex = messagesToShow.length - 1 - targetIndex;
  } else if (targetIndex >= 0) {
    messagesToShow = history.slice(0, targetIndex + 1);
    displayTargetIndex = targetIndex;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-amber-400/80 mb-2 italic">
        ⚠️ This is synthetic test data generated for evaluation purposes, not real user conversations. The assistant messages shown are template-based placeholders used to set up conversation context before the coach responds.
      </p>
      <div className="space-y-3">
        {messagesToShow.map((msg, displayIdx) => {
          const isUser = msg.role === 'user';
          const isTargetMessage = isUser && displayIdx === displayTargetIndex;
        
          return (
            <div key={`msg-${displayIdx}-${(msg.content || msg.text || '').substring(0, 20)}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-3 ${
                isUser 
                  ? isTargetMessage
                    ? 'bg-blue-600/30 border-2 border-blue-400 text-blue-100 ring-2 ring-blue-500/50' 
                    : 'bg-blue-600/20 border border-blue-500/40 text-blue-100'
                  : 'bg-gray-700/50 border border-gray-600/40 text-gray-200'
              }`}>
                <div className="text-xs font-medium mb-1 opacity-70 flex items-center gap-2">
                  {isUser ? 'User' : 'Assistant'}
                  {isTargetMessage && (
                    <span className="px-2 py-0.5 bg-blue-500/30 text-blue-200 text-xs rounded border border-blue-400/50">
                      ← Coach Responded To This
                    </span>
                  )}
                </div>
                <div className="whitespace-pre-wrap text-sm">{msg.content || msg.text || ''}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        The coach response below is responding to the message marked "← Coach Responded To This". Previous assistant messages after that point are hidden as they're not part of this evaluation.
      </p>
    </div>
  );
};

ConversationHistoryRenderer.propTypes = {
  history: PropTypes.array,
  respondedToIndex: PropTypes.number,
};

/**
 * Renders just the last message preview (for collapsed state)
 */
export const LastMessagePreview = ({ history, respondedToIndex }) => {
  const targetMessage = useMemo(() => {
    const { targetIndex, normalizedHistory } = getTargetMessageInfo(history, respondedToIndex);
    if (targetIndex < 0 || !normalizedHistory) return null;

    const targetMsg = normalizedHistory[targetIndex];
    if (targetMsg && targetMsg.role === 'user') {
      return targetMsg.content || targetMsg.text || '';
    }
    return null;
  }, [history, respondedToIndex]);

  if (!targetMessage) return null;

  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-lg p-3 bg-blue-600/20 border border-blue-500/40 text-blue-100">
        <div className="text-xs font-medium mb-1 opacity-70">User</div>
        <div className="whitespace-pre-wrap text-sm">{targetMessage}</div>
      </div>
    </div>
  );
};

LastMessagePreview.propTypes = {
  history: PropTypes.array,
  respondedToIndex: PropTypes.number,
};

